import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import {
  hasUnsavedChanges,
  isGuardBypassed,
  promptLeave,
  runWithGuardBypass,
  subscribeDirtyChanges,
} from "@/lib/unsavedChangesRegistry";

/**
 * Intercepts browser back/forward when registered forms have unsaved changes.
 * Mount once near the app root (inside UnsavedChangesProvider).
 */
export function BrowserHistoryGuard() {
  const [path] = useLocation();
  const trappingRef = useRef(false);

  useEffect(() => {
    const syncTrap = () => {
      if (isGuardBypassed()) return;

      if (hasUnsavedChanges()) {
        if (!trappingRef.current) {
          window.history.pushState(null, "", window.location.href);
          trappingRef.current = true;
        }
        return;
      }

      trappingRef.current = false;
    };

    const onPopState = () => {
      if (isGuardBypassed()) return;

      if (!hasUnsavedChanges()) {
        trappingRef.current = false;
        return;
      }

      // Re-trap so the URL stays on the current page until the user confirms.
      window.history.pushState(null, "", window.location.href);
      trappingRef.current = true;

      promptLeave(() => {
        runWithGuardBypass(() => {
          trappingRef.current = false;
          // Stack is typically [previousPage, currentPage, syntheticTrap].
          // A single history.back() only removes the trap — go(-2) reaches
          // the page the user originally tried to navigate to.
          window.history.go(-2);
        });
      });
    };

    syncTrap();
    const unsubscribeDirty = subscribeDirtyChanges(syncTrap);
    window.addEventListener("popstate", onPopState);

    return () => {
      unsubscribeDirty();
      window.removeEventListener("popstate", onPopState);
    };
  }, [path]);

  return null;
}
